// Renders a SAVED chart by id: fetches data + component_code from the backend
// and hands the code string to DynamicChart. No chart-type switching.
import { Inbox, RefreshCw, Unplug } from 'lucide-react';

import { ApiError } from '../../../lib/api';
import { Button, Skeleton } from '../../../ui';
import { useChartData } from '../hooks';
import { labelLinePx, minTileHeightPx } from '../dashboard/legibility';
import { deriveAxisLabels } from '../lib/axisLabels';
import { mapRowsToComponentProps } from '../lib/chartProps';
import { ChartFrame } from './ChartFrame';
import { DynamicChart } from './DynamicChart';

interface ChartRendererProps {
  chartId: string;
  filterContext?: Record<string, unknown>;
  /** The box this chart is being drawn in, in pixels. REQUIRED, and deliberately
   *  so: it used to default to 300, which is a size this component had no way to
   *  know and every caller already knew (AG-2105). Both real callers are
   *  thumbnails and pass their own. */
  height: number;
  /** Suppress the retry affordance in thumbnails, where there is no room. */
  compact?: boolean;
}

/** A dead upstream source explained in one line, never as a raw proxy error. */
function friendlyError(error: unknown): string {
  const status = error instanceof ApiError ? error.status : undefined;
  if (status === 502) return 'That data source could not be reached.';
  if (status === 404) return 'This chart no longer exists.';
  if (status && status >= 500) return 'The data source had a problem.';
  return error instanceof Error ? error.message : 'Could not load this chart.';
}

export function ChartRenderer({ chartId, filterContext = {}, height, compact }: ChartRendererProps) {
  const { data, isLoading, error, refetch, isFetching } = useChartData(chartId, filterContext);
  // Room for a button under a line of text, at the app's own scale — not a
  // number picked here.
  const showRetry = !compact && height >= labelLinePx() * 4;

  if (isLoading) {
    return (
      <div className="flex h-full w-full flex-col gap-2 p-2" style={{ height }} aria-busy>
        <Skeleton className="h-2.5 w-24" />
        <Skeleton className="min-h-0 flex-1" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-center"
        style={{ height }}
        role="status"
      >
        <Unplug className="size-4 text-fg-faint" aria-hidden />
        <span className="text-xs text-fg-muted">{friendlyError(error)}</span>
        {showRetry && (
          <Button size="sm" variant="secondary" icon={<RefreshCw />} loading={isFetching} onClick={() => void refetch()}>
            Retry
          </Button>
        )}
      </div>
    );
  }

  if (!data) return null;

  // The query succeeded but the source returned zero rows — say that plainly,
  // filling the box a real chart would occupy, instead of an empty axis grid
  // that reads as broken.
  if (!data.rows?.length) {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-center"
        style={{ height }}
        role="status"
      >
        <Inbox className="size-4 text-fg-faint" aria-hidden />
        <span className="text-xs text-fg-muted">No rows came back</span>
        {showRetry && (
          <Button size="sm" variant="ghost" icon={<RefreshCw />} loading={isFetching} onClick={() => void refetch()}>
            Retry
          </Button>
        )}
      </div>
    );
  }

  const scope = mapRowsToComponentProps(
    data.chart_type,
    data.rows,
    data.field_map,
    { height, ...data.properties },
    data.component_props,
  );
  // ⭐ ITS OWN QUESTION, ASKED IN ITS OWN TERMS (AG-2205).
  //
  // Axis titles are drawn only when there is room for them AROUND the plot. That
  // used to be spelled `height >= minTileHeightPx(chart_type)` on the reasoning
  // that it was "the same question" — and it was not. `minTileHeightPx` answers
  // how much room a TILE gets before anything is drawn in it, and when that
  // stopped consulting the chart's form its value moved, which silently turned
  // axis titles ON for boards that had been laid out without them. Three
  // overflow specs caught it: a category label ran 22px past its box.
  //
  // What this actually needs is the plot plus a title line above and below it,
  // so that is what it asks for. No chart type, and no borrowed threshold.
  const titleLines = labelLinePx() * 2;
  const { xLabel, yLabel } = height >= minTileHeightPx(undefined) + titleLines
    ? deriveAxisLabels(data.field_map, data.properties)
    : { xLabel: '', yLabel: '' };
  return (
    <ChartFrame xLabel={xLabel} yLabel={yLabel}>
      <DynamicChart code={data.component_code} scope={scope} />
    </ChartFrame>
  );
}
